/**
 * Экран «Подписка сервиса LovArt Hotels» владельца отеля — §13.1 manager v3.
 *
 * Композиция строго по макету §13.1:
 *  - topbar: крошки «Финансы / Подписка» + h2 «Подписка сервиса LovArt Hotels» + CTA
 *    «Сменить план» (ghost) + «Продлить»;
 *  - grid-2: карточка «Текущий план» (KPI цена/номер, строки Номеров/Сумма/
 *    Оплачено до/Автопродление, «Скачать акт») + карточка «Что в подписке»
 *    (✓ активные / — addon фичи);
 *  - широкая карточка «История платежей» — таблица Дата·Сумма·Период·Способ·
 *    Статус·PDF, у неоплаченного счёта кнопка «Оплатить (Т-Банк)».
 *  - Состояния: skeleton-загрузка, баннер Expired (красный), Renewal soon
 *    (warn), считаются по «оплачено до» (period_ends_at).
 *  - Мобайл 375: карточка плана в charcoal #4d4239 / champagne #d4c2a8, KPI
 *    вертикально, строки истории min-height 44px, sticky CTA снизу.
 *
 * «Сменить план» — ОТДЕЛЬНЫЙ ЭКРАН с «Назад» (canon формы, НЕ модалка):
 * SubscriptionPage держит локальное состояние view и рендерит ТОЛЬКО
 * ChangePlanScreen, скрывая список (см. feedback_every_form_is_a_separate_screen).
 *
 * Бэк (ветка #469, контракты не меняем):
 *   GET  /v1/subscriptions/me     — тариф/цена/период/автопродление;
 *   GET  /v1/subscriptions/plans  — каталог для «Сменить план»;
 *   GET  /v1/invoices             — история платежей;
 *   POST /v1/invoices/{id}/pay    — Т-Банк pay-URL → редирект.
 * PDF-генерации акта/счёта на бэке нет → «Скачать акт» / «PDF» отключены
 * (disabled + title), честно, без имитации.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import { redirectToTbank } from '@hsc/api-client';
import type { Invoice, Subscription, Tier } from '@hsc/types';
import {
  useInvoices,
  useMySubscription,
  useProvisionSubscription,
  useRoomCount,
} from './useSubscription.js';
import { usePayInvoice } from './useSubscription.js';
import { formatMoney } from './format.js';
import { ChangePlanScreen } from './ChangePlanScreen.js';

/** tier → русское название тарифа (бэк отдаёт basic/pro/enterprise). */
const TIER_LABELS: Record<Tier, string> = {
  basic: 'LovArt Старт',
  pro: 'LovArt Резорт',
  enterprise: 'LovArt Сеть',
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'черновик',
  sent: 'выставлен',
  paid: 'оплачено',
  overdue: 'просрочен',
  canceled: 'отменён',
};

/** Что входит в подписку — фичи плана. Если бэк начнёт отдавать capabilities,
 * заменить статикой оттуда; пока статичный список по §13.1. */
const PLAN_FEATURES: readonly string[] = [
  'Полный гость-сервис (заявки, меню, room-service)',
  'Шахматка + календарь',
  'Персонал + расписание + handover',
  'Reviews + анонсы',
  'Reports (25 шт)',
  'Поддержка 24/7 (Telegram + email)',
];
const PLAN_ADDONS: readonly string[] = [
  'Inventory ADDON (3 500 ₽/мес, опц)',
  'White-label (договорная)',
];

function rub(kopecks: number): string {
  return formatMoney(kopecks / 100, 'RUB');
}

/** Полная дата ДД.ММ.ГГГГ (оплачено до). */
function formatFullDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Период счёта «Июнь 2026» по дате выставления. */
function formatPeriod(iso: string): string {
  try {
    const s = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(
      new Date(iso),
    );
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch {
    return iso;
  }
}

/** Дней до/после «оплачено до». >0 — осталось, <0 — истекло N дней назад. */
function daysUntil(iso: string): number {
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return Number.POSITIVE_INFINITY;
  return Math.round((end - Date.now()) / 86_400_000);
}

/* ---------------- блок «нет подписки» (активация) ---------------- */

function ActivateTariff(): JSX.Element {
  const rooms = useRoomCount();
  const provision = useProvisionSubscription();
  const roomCount = rooms.data ?? 0;
  return (
    <section data-testid="subscription-activate">
      <div className="ta-pagehead bill-topbar">
        <div>
          <div className="ta-pagehead__crumbs">Финансы / Подписка</div>
          <h2 className="ta-pagehead__title">Подписка сервиса LovArt Hotels</h2>
        </div>
      </div>
      <div className="bill-body">
        <Card data-testid="subscription-empty">
          <p>Тариф для отеля пока не активирован.</p>
          <p className="ta-muted">
            Тариф «LovArt Старт» — 400 ₽ за номер в месяц
            {rooms.isSuccess
              ? ` × ${roomCount} = ${rub(Math.max(roomCount * 400 * 100, 1_000_000))}/мес`
              : ''}
            . Минимальная такса 10 000 ₽/мес.
          </p>
          <div className="folio-form__row">
            <Button
              data-testid="subscription-activate-btn"
              disabled={!rooms.isSuccess || roomCount <= 0 || provision.isPending}
              onClick={() => provision.mutate({ plan: 'start', room_count: roomCount })}
            >
              {provision.isPending ? 'Активируем…' : 'Активировать тариф «Старт»'}
            </Button>
          </div>
          {provision.isError ? (
            <p role="alert" className="ta-error">
              Не удалось активировать тариф. Попробуйте позже.
            </p>
          ) : null}
        </Card>
      </div>
    </section>
  );
}

/* ---------------- баннер состояния (expired / renewal soon) ---------------- */

function StateBanner({ sub }: { sub: Subscription }): JSX.Element | null {
  const left = daysUntil(sub.period_ends_at);
  if (left < 0) {
    const ago = Math.abs(left);
    return (
      <div className="bill-banner bill-banner--danger" role="alert" data-testid="subscription-expired">
        Подписка истекла {ago}{' '}
        {ago === 1 ? 'день' : ago < 5 ? 'дня' : 'дней'} назад — оплатите, чтобы вернуть доступ.
      </div>
    );
  }
  if (left <= 7) {
    return (
      <div className="bill-banner bill-banner--warn" data-testid="subscription-renewal-soon">
        Оплата через {left} {left === 1 ? 'день' : left < 5 ? 'дня' : 'дней'}.
      </div>
    );
  }
  return null;
}

/* ---------------- карточка «Текущий план» ---------------- */

function CurrentPlanCard({ sub }: { sub: Subscription }): JSX.Element {
  const tierName = TIER_LABELS[sub.tier] ?? sub.tier;
  const auto = sub.auto_renew;
  return (
    <Card className="bill-plan-card" data-testid="subscription-tariff">
      <h4 className="bill-card__title">Текущий план</h4>
      <div className="bill-kpi">
        <div className="bill-kpi__label" data-testid="subscription-tier">
          {tierName}
        </div>
      </div>
      <hr className="bill-divider" />
      <dl className="bill-rows">
        <div className="bill-rows__row">
          <dt>Номеров:</dt>
          <dd data-testid="subscription-rooms">{sub.room_count}</dd>
        </div>
        <div className="bill-rows__row">
          <dt>Сумма / месяц:</dt>
          <dd data-testid="subscription-monthly">{rub(sub.period_amount_kopecks)}</dd>
        </div>
        <div className="bill-rows__row">
          <dt>Оплачено до:</dt>
          <dd data-testid="subscription-paid-until">{formatFullDate(sub.period_ends_at)}</dd>
        </div>
        <div className="bill-rows__row">
          <dt>Автопродление:</dt>
          <dd>
            <span
              className={`ta-chip ${auto ? 'ta-chip--done' : 'ta-chip--muted'}`}
              data-testid="subscription-autorenew"
            >
              {auto ? 'ВКЛ' : 'ВЫКЛ'}
            </span>
          </dd>
        </div>
      </dl>
      <Button
        variant="ghost"
        size="sm"
        className="bill-plan-card__act"
        disabled
        title="Генерация PDF-акта появится позже"
        data-testid="subscription-download-act"
      >
        Скачать акт
      </Button>
    </Card>
  );
}

/* ---------------- карточка «Что в подписке» ---------------- */

function PlanFeaturesCard(): JSX.Element {
  return (
    <Card className="bill-feat-card" data-testid="subscription-features">
      <h4 className="bill-card__title">Что в подписке</h4>
      <ul className="bill-feat">
        {PLAN_FEATURES.map((f) => (
          <li key={f} className="bill-feat__on">
            ✓ {f}
          </li>
        ))}
        {PLAN_ADDONS.map((f) => (
          <li key={f} className="bill-feat__off">
            — {f}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* ---------------- история платежей: таблица ---------------- */

function PayCell({ inv }: { inv: Invoice }): JSX.Element | null {
  const pay = usePayInvoice();
  const payable = inv.state === 'sent' || inv.state === 'overdue';
  if (!payable) return null;
  const onPay = () => {
    pay.mutate(
      { invoice: inv },
      {
        onSuccess: (payment) => {
          if (payment.tbank_payment_url) redirectToTbank(payment.tbank_payment_url);
        },
      },
    );
  };
  return (
    <>
      <Button
        size="sm"
        data-testid={`invoice-pay-${inv.id}`}
        disabled={pay.isPending}
        onClick={onPay}
      >
        {pay.isPending ? 'Открываем…' : 'Оплатить (Т-Банк)'}
      </Button>
      {pay.isError ? (
        <span role="alert" className="ta-error" data-testid={`invoice-pay-error-${inv.id}`}>
          Ошибка оплаты
        </span>
      ) : null}
    </>
  );
}

function statusChipClass(state: string): string {
  if (state === 'paid') return 'ta-chip--done';
  if (state === 'overdue') return 'ta-chip--alert';
  if (state === 'sent') return 'ta-chip--pending';
  return 'ta-chip--muted';
}

function PaymentsHistory(): JSX.Element {
  const { data, isLoading, isError, refetch } = useInvoices();

  if (isLoading) {
    return (
      <Card className="bill-history" data-testid="invoices-skeleton">
        <h4 className="bill-card__title">История платежей</h4>
        <Skeleton height={18} radius={6} />
        <Skeleton height={18} radius={6} />
        <Skeleton height={18} radius={6} />
      </Card>
    );
  }
  if (isError || data === undefined) {
    return (
      <Card className="bill-history" data-testid="invoices-error">
        <h4 className="bill-card__title">История платежей</h4>
        <p role="alert">Не удалось загрузить счета.</p>
        <Button variant="secondary" onClick={() => void refetch()}>
          Повторить
        </Button>
      </Card>
    );
  }

  return (
    <Card className="bill-history" data-testid="invoices-block">
      <h4 className="bill-card__title">История платежей</h4>
      {data.invoices.length === 0 ? (
        <p className="ta-muted" data-testid="invoices-empty">
          Платежей пока не было.
        </p>
      ) : (
        <>
          {/* desktop / tablet — таблица */}
          <table className="bill-table" data-testid="invoices-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Сумма</th>
                <th>Период</th>
                <th>Способ</th>
                <th>Статус</th>
                <th aria-label="Документ" />
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td>{formatFullDate(inv.issued_at)}</td>
                  <td className="bill-table__money">{rub(inv.total_kopecks)}</td>
                  <td>{formatPeriod(inv.issued_at)}</td>
                  <td>{inv.state === 'paid' ? 'Т-Банк' : '—'}</td>
                  <td>
                    <span className={`ta-chip ${statusChipClass(inv.state)}`}>
                      {INVOICE_STATUS_LABELS[inv.state] ?? inv.state}
                    </span>
                  </td>
                  <td className="bill-table__act">
                    <PayCell inv={inv} />
                    <button
                      type="button"
                      className="bill-pdf"
                      disabled
                      title="Генерация PDF появится позже"
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* phone — карточки-строки min-height 44px */}
          <ul className="bill-history__rows" data-testid="invoices-rows-mobile">
            {data.invoices.map((inv) => (
              <li key={inv.id} className="bill-history__row">
                <div>
                  <div className="bill-history__date">{formatFullDate(inv.issued_at)}</div>
                  <div className="bill-history__sub">
                    {formatPeriod(inv.issued_at)} · {inv.state === 'paid' ? 'Т-Банк' : '—'}
                  </div>
                </div>
                <div className="bill-history__right">
                  <strong className="bill-table__money">{rub(inv.total_kopecks)}</strong>
                  <span className={`ta-chip ${statusChipClass(inv.state)}`}>
                    {INVOICE_STATUS_LABELS[inv.state] ?? inv.state}
                  </span>
                  <PayCell inv={inv} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

/* ---------------- экран ---------------- */

/** Первый неоплаченный счёт (для CTA «Продлить» в topbar). */
function useNextPayable(): Invoice | undefined {
  const { data } = useInvoices();
  return useMemo(
    () => data?.invoices.find((i) => i.state === 'sent' || i.state === 'overdue'),
    [data],
  );
}

function SubscriptionView(): JSX.Element {
  const sub = useMySubscription();
  const [view, setView] = useState<'main' | 'change-plan'>('main');
  const pay = usePayInvoice();
  const nextPayable = useNextPayable();

  if (sub.isLoading) {
    return (
      <section data-testid="subscription-page">
        <div className="ta-pagehead bill-topbar">
          <div>
            <div className="ta-pagehead__crumbs">Финансы / Подписка</div>
            <h2 className="ta-pagehead__title">Подписка сервиса LovArt Hotels</h2>
          </div>
        </div>
        <div className="bill-body" aria-busy="true" data-testid="subscription-skeleton">
          <div className="bill-grid-2">
            <Card className="bill-plan-card">
              <Skeleton height={24} radius={6} />
              <Skeleton height={40} radius={6} />
            </Card>
            <Card className="bill-feat-card">
              <Skeleton height={120} radius={6} />
            </Card>
          </div>
          <Card className="bill-history">
            <Skeleton height={80} radius={6} />
          </Card>
        </div>
      </section>
    );
  }

  const status =
    sub.error && typeof sub.error === 'object' && 'status' in sub.error
      ? (sub.error as { status?: number }).status
      : undefined;
  if (sub.isError && status === 404) {
    return <ActivateTariff />;
  }
  if (sub.isError || sub.data === undefined) {
    return (
      <section data-testid="subscription-page">
        <div className="ta-pagehead bill-topbar">
          <div>
            <div className="ta-pagehead__crumbs">Финансы / Подписка</div>
            <h2 className="ta-pagehead__title">Подписка сервиса LovArt Hotels</h2>
          </div>
        </div>
        <div className="bill-body">
          <Card data-testid="subscription-error">
            <p role="alert">Не удалось загрузить подписку.</p>
            <Button variant="secondary" onClick={() => void sub.refetch()}>
              Повторить
            </Button>
          </Card>
        </div>
      </section>
    );
  }

  // «Сменить план» — ОТДЕЛЬНЫЙ ЭКРАН (рендерим только его, список скрыт).
  if (view === 'change-plan') {
    return <ChangePlanScreen current={sub.data} onBack={() => setView('main')} />;
  }

  const onRenew = () => {
    if (!nextPayable) return;
    pay.mutate(
      { invoice: nextPayable },
      {
        onSuccess: (payment) => {
          if (payment.tbank_payment_url) redirectToTbank(payment.tbank_payment_url);
        },
      },
    );
  };

  return (
    <section data-testid="subscription-page">
      <div className="ta-pagehead bill-topbar">
        <div>
          <div className="ta-pagehead__crumbs">Финансы / Подписка</div>
          <h2 className="ta-pagehead__title">Подписка сервиса LovArt Hotels</h2>
        </div>
        <div className="bill-topbar__cta">
          <Button
            variant="ghost"
            size="sm"
            data-testid="subscription-change-plan"
            onClick={() => setView('change-plan')}
          >
            Сменить план
          </Button>
          <Button
            size="sm"
            data-testid="subscription-renew"
            disabled={!nextPayable || pay.isPending}
            title={nextPayable ? undefined : 'Нет счёта к оплате'}
            onClick={onRenew}
          >
            {pay.isPending ? 'Открываем…' : 'Продлить'}
          </Button>
        </div>
      </div>

      <div className="bill-body">
        <StateBanner sub={sub.data} />
        <div className="bill-grid-2">
          {/* charcoal-карточка плана включается через CSS на 375 */}
          <CurrentPlanCard sub={sub.data} />
          <PlanFeaturesCard />
        </div>
        <PaymentsHistory />
      </div>

      {/* sticky CTA снизу — только phone (CSS) */}
      <div className="bill-sticky-cta" data-testid="subscription-sticky-cta">
        <Button
          variant="ghost"
          size="sm"
          className="bill-sticky-cta__btn"
          onClick={() => setView('change-plan')}
        >
          Сменить план
        </Button>
        <Button
          size="sm"
          className="bill-sticky-cta__btn"
          disabled={!nextPayable || pay.isPending}
          onClick={onRenew}
        >
          Продлить
        </Button>
      </div>
    </section>
  );
}

export function SubscriptionPage(): JSX.Element {
  return <SubscriptionView />;
}
